import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { SignalCheckbox, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import { form, FormField as SignalFormField, required, schema } from '@angular/forms/signals';
import type { FormField } from '../../../../models/built-form';

interface UrlConfigModel {
  label: string;
  helpText: string;
  required: boolean;
}

@Component({
  selector: 'app-url-config-view',
  standalone: true,
  templateUrl: './url-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SignalCheckbox, SignalTextField, SignalFormField, SignalFormDirective],
})
export class UrlConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<UrlConfigModel>({
    label: '',
    helpText: '',
    required: false,
  });
  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });
  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;

      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        required: next.required ?? next.validation?.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        validation: {
          ...field.validation,
          required: draft.required,
        },
      });
    });
  }
}
